import React from "react";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@material-ui/core";
import {round} from "lodash";
import {UseTranslateResult} from "@tolgee/react";
import {Setting} from "view/components/SettingsList/types";

import {
  BankTransferOrder,
  ExpandedOrder,
  Invoice,
  OrderProductStatus,
  OrderStatus,
} from "ts/api/order";
import {getDisplayValue} from "tools/common/getDisplayValue";
import formatPrice from "tools/formats/formatPrice";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import {PaymentMethodType} from "ts/api/paymentMethod";
import {
  Currency,
  IExhibitorPair,
  IRefund,
  IResolvedLocalMap,
  IShow,
  ISupplier,
  ISupplierCompany,
  RefundStatus,
  Roles,
  ShowDateFormat,
} from "@evennt-tech/api-v2-core";
import filterNotEmptyArray from "tools/common/filterNotEmptyArray";
import downloadPdfFile from "tools/file/downloadPdfFile";
import Auth from "ts/api/auth";
import orderStatusesMap, {
  getOrderStatusMap,
  getReviewOrderStatusMap,
  isBankTransferAwaitingPayment,
  isPreAuthorisedOrder,
} from "tools/order/getOrderStatus";
import {getDueDateTooltip} from "tools/order/getDueDateTooltip";
import {
  getOrderDueDate,
  isInvoiceOverdue,
} from "tools/order/getPaymentStatusBuckets";

import {CpoConfigById} from "store/custom-product-option/custom-product-option-configs.store";
import {getDiscountAmount} from "view/apps/Exhibitor/pages/Cart/helpers";
import calculateTotalSupplierPrice from "tools/product/productCalculations/calculateTotalSupplierPrice";
import paymentTypesMap from "tools/order/paymentTypesMap";

import {MoneyHelper, RefundCalculator} from "@evennt-tech/api-v2-payment";
import FlexBox from "view/components/Layout/FlexBox";
import {orange} from "@material-ui/core/colors";
import {Timestamp} from "api/firebase";
import {Theme} from "@material-ui/core/styles";
import formatDate from "tools/formats/dayJsFormat";
import {format} from "date-fns";
import {getReadableString} from "constraints/general";
import {ShowUtils} from "tools/show";
import WatchLaterIcon from "@material-ui/icons/WatchLater";
import PaymentStatusLabel from "../PaymentStatusLabel";
import OrderStatusLabel from "./components/OrderStatusLabel";
import {generateBankTransferInvoicePdfBlob} from "../PdfTemplates/BankTransferInvoicePdfView";
import {generateVatPdfBlob} from "../PdfTemplates/CardInvoicePdfView";

type TranslateFn = UseTranslateResult["t"];

export async function loadImageWithRedirect(
  url?: string
): Promise<string | null> {
  if (!url) return null;

  try {
    const response = await fetch(url, {redirect: "follow"});
    if (!response.ok) {
      console.warn(`Failed to fetch image: ${response.status}`);
      return null;
    }

    const blob = await response.blob();
    const reader = new FileReader();

    return new Promise((resolve, reject) => {
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.error("Image loading error:", err);
    return null;
  }
}

export const exhibitorDetails = (
  order: ExpandedOrder,
  role: Roles,
  t: TranslateFn,
  localMap: IResolvedLocalMap
): Setting<Record<string, any>>[] => {
  const items: Array<Setting<Record<string, any>> | null> = [
    {
      title: t("exhibitor.dashboard.update.exhibitor_name", {
        exhibitor: localMap.exhibitor,
      }),
      value:
        order.exhibitorConnection?.exhibitorPair?.companyToShow?.company
          ?.name || order.billingDetails.tradingName,
      type: "read-only",
    },
    {
      title: t("common.order_drawer.company_name"),
      value:
        order.billingDetails.tradingName ||
        order.exhibitorConnection?.exhibitorPair.companyToShow?.company?.name,
      type: "read-only",
    },
    order.exhibitorConnection?.exhibitorPair?.companyToShow?.company
      ?.suffixName && role !== Roles.EXHIBITOR
      ? {
          title: t("common.order_drawer.company_name_suffix"),
          value:
            order.exhibitorConnection?.exhibitorPair?.companyToShow?.company
              ?.suffixName || "",
          type: "read-only",
        }
      : null,
    {
      title: t("exhibitor.order.field.address"),
      value: order.billingDetails.address,
      type: "read-only",
    },
    {
      title: localMap.postCode,
      value: order.billingDetails.postCode,
      type: "read-only",
    },
    {
      title: t("common.order_drawer.vat"),
      value: order.billingDetails.vat,
      type: "read-only",
    },
    {
      title: t("exhibitor.cart.details.contact_details"),
      value: getDisplayValue([
        order.billingDetails.contact,
        order.billingDetails.contactEmail,
        order.billingDetails.contactPhoneNumber,
      ]),
      type: "read-only",
    },
  ];

  return items.filter(Boolean) as Setting<Record<string, any>>[];
};

type SupplierPaymentDetailsParams = {
  order: ExpandedOrder;
  supplier: ISupplier;
  show: IShow;
  refunds: IRefund[];
  supplierCompany: ISupplierCompany;
  palette: Theme["palette"];
  t: TranslateFn;
  onRefundClick?: () => void;
  isRefundsEnabled?: boolean;
};

export const supplierPaymentDetails = ({
  order,
  supplierCompany,
  supplier,
  show,
  refunds,
  onRefundClick,
  palette,
  isRefundsEnabled,
  t,
}: SupplierPaymentDetailsParams): Setting<Record<string, any>>[] => {
  const invoice =
    order.paymentType === PaymentMethodType.bankTransfer
      ? Object.values(order.invoices).find(
          (invoice) => invoice.products?.[0]?.owner?.id === supplierCompany.id
        )
      : null;
  const supplierProducts = order.products.filter(
    (p) => p.owner.id === supplierCompany.id
  );

  const getTotalAmount = () => {
    if (invoice) {
      return invoice.totalAmount;
    }
    const isAmountEmpty =
      ![PaymentMethodType.cardPayment, PaymentMethodType.idealPayment].includes(
        order.paymentType
      ) && !order.isQuick;
    if (isAmountEmpty) {
      return 0;
    }

    return calculateTotalSupplierPrice(supplierProducts, show).totalAmount;
  };

  const amountReceivedSetting: Setting<Record<string, any>>[] = invoice
    ? [
        {
          title: t("common.order_drawer.amount_received"),
          value: formatPrice(invoice.amountPaid, show.currency),
          type: "read-only",
        },
      ]
    : [];

  const discountAmount = invoice
    ? invoice.discountAmount ?? 0
    : getDiscountAmount(supplierProducts);
  const discountAmountSetting: Setting<Record<string, any>>[] = discountAmount
    ? [
        {
          title: t("exhibitor.order.field.discount"),
          value: formatPrice(discountAmount, show.currency),
          type: "read-only",
        },
      ]
    : [];

  const totalAmount = getTotalAmount();

  const orderDueDate = getOrderDueDate(
    order,
    supplierCompany.id,
    show.liveStart
  );
  const dueStatus = isInvoiceOverdue(orderDueDate) ? "overdue" : "due";
  const dueDateAsDate =
    orderDueDate instanceof Date ? orderDueDate : orderDueDate?.toDate();

  return [
    {
      title: t("exhibitor.order.field.status"),
      value: isBankTransferAwaitingPayment(order) ? (
        <Tooltip title={getDueDateTooltip(dueStatus, dueDateAsDate)}>
          <span style={{display: "inline-flex"}}>
            <FlexBox alignCenter gap={1}>
              <WatchLaterIcon
                htmlColor={orange[500]}
                style={{fontSize: "16px"}}
              />
              <PaymentStatusLabel
                order={order}
                companyId={supplierCompany.id}
              />
            </FlexBox>
          </span>
        </Tooltip>
      ) : (
        <PaymentStatusLabel order={order} companyId={supplierCompany.id} />
      ),
      type: "read-only",
    },
    ...discountAmountSetting,
    ...getAmountDetails({
      amount: totalAmount,
      currency: show.currency,
      paidAt: order.paymentDetails.paymentDate,
      isPreAuthorised: isPreAuthorisedOrder(order),
      paymentMethod: order.paymentType,
      palette,
      dateFormat: show.dateFormat!,
      t,
    }),
    ...(order.paymentType === PaymentMethodType.bankTransfer
      ? amountReceivedSetting
      : []),
    ...getRefundDetails(
      refunds,
      show.currency,
      t,
      onRefundClick,
      supplier,
      isRefundsEnabled
    ),
  ];
};

export const exhibitorPaymentDetails = (
  order: ExpandedOrder,
  show: IShow,
  refunds: IRefund[],
  isExhibitorView: boolean,
  palette: Theme["palette"],
  t: TranslateFn,
  onRefundClick?: () => void
): Setting<Record<string, any>>[] => {
  const theme = useTheme();
  const paymentReceived = round(
    Object.values((order as BankTransferOrder)?.invoices || [])?.reduce(
      (acc, cur) => acc + (cur?.amountPaid || 0),
      0
    ),
    2
  );
  const showInfoTooltip =
    paymentReceived > 0 && paymentReceived !== order.paymentDetails.amount;
  const amountReceivedSetting: Setting<Record<string, any>> = {
    title: t("common.order_drawer.amount_received"),
    value: showInfoTooltip ? (
      <Typography
        variant="body2"
        style={{display: "flex", alignItems: "center"}}
      >
        <Box mr={0.5}>{formatPrice(paymentReceived, show.currency)}</Box>
        <Tooltip
          title={t("common.order_drawer.amount_mismatch_tooltip")}
          aria-label="info"
        >
          <IconButton aria-label="info">
            <InfoOutlinedIcon
              style={{color: theme.palette.error.main, width: 20, height: 20}}
            />
          </IconButton>
        </Tooltip>
      </Typography>
    ) : (
      formatPrice(paymentReceived, show.currency)
    ),
    type: "read-only",
  };

  const isPartiallyCancelled =
    !!order.isPartiallyCancelled &&
    !!order.paymentDetails.amount &&
    order.products.some((p) => p.status !== OrderProductStatus.cancelled);

  const amount = isPartiallyCancelled
    ? calculateSpentAmount(order, show.salesTaxPercentage)
    : order.paymentDetails.amount;

  const discountAmount = getDiscountAmount(order.products);
  const discountAmountSetting = discountAmount
    ? [
        {
          title: t("exhibitor.order.field.discount"),
          value: formatPrice(discountAmount, show.currency),
          type: "read-only",
        } as const,
      ]
    : [];

  return [
    {
      title: t("exhibitor.order.field.status"),
      value: <PaymentStatusLabel order={order} />,
      type: "read-only",
    },
    ...discountAmountSetting,
    ...getAmountDetails({
      amount,
      currency: show.currency,
      paidAt: order.paymentDetails.paymentDate,
      isPreAuthorised: isPreAuthorisedOrder(order),
      paymentMethod: order.paymentType,
      palette,
      dateFormat: show.dateFormat!,
      t,
    }),
    ...(order.paymentType === PaymentMethodType.bankTransfer
      ? [amountReceivedSetting]
      : []),
    ...getRefundDetails(refunds, show.currency, t, onRefundClick),
  ];
};

const patternByFormat: Record<ShowDateFormat, string> = {
  [ShowDateFormat.dayFirst]: "DD/MM/YYYY HH:mm",
  [ShowDateFormat.monthFirst]: "MM/DD/YYYY HH:mm",
  [ShowDateFormat.yearFirst]: "YYYY/MM/DD HH:mm",
};

type GetAmountDetailsParams = {
  amount: number;
  currency: Currency;
  paymentMethod: PaymentMethodType;
  paidAt?: Timestamp;
  isPreAuthorised?: boolean;
  palette: Theme["palette"];
  dateFormat: ShowDateFormat;
  t: TranslateFn;
};
const getAmountDetails = ({
  amount,
  paidAt,
  isPreAuthorised,
  paymentMethod,
  currency,
  palette,
  dateFormat,
  t,
}: GetAmountDetailsParams) => {
  return [
    {
      title: t("common.order_drawer.amount"),
      type: "read-only",
      value: (
        <FlexBox justifyBetween alignCenter fullWidth>
          <Typography variant="h4" style={{color: palette.contrastText.main}}>
            {formatPrice(amount, currency)}
          </Typography>
          {!!paidAt && (
            <FlexBox gap={1} alignCenter>
              <Typography
                variant="h4"
                style={{color: palette.defaultSideText.main}}
              >
                {t("common.order_drawer.paid_by", {
                  method: paymentTypesMap[paymentMethod].label,
                })}
              </Typography>
              <Tooltip
                title={t({
                  key: isPreAuthorised
                    ? "common.order_drawer.pre_authorised_date"
                    : "common.order_drawer.payment_date",
                  defaultValue: isPreAuthorised
                    ? "Pre-Authorised: {date}"
                    : "Payment date: {date}",
                  params: {
                    // The design draws the pre-authorisation date the way the
                    // orders list does: the show's readable format, no time.
                    date: isPreAuthorised
                      ? format(paidAt.toDate(), getReadableString({dateFormat}))
                      : formatDate(
                          paidAt.toDate(),
                          patternByFormat[dateFormat]
                        ),
                  },
                })}
              >
                <InfoOutlinedIcon htmlColor={palette.defaultSideText.main} />
              </Tooltip>
            </FlexBox>
          )}
        </FlexBox>
      ),
    } as Setting<Record<string, any>>,
  ];
};

const getRefundDetails = (
  refunds: IRefund[],
  currency: Currency,
  t: TranslateFn,
  onRefundClick?: () => void,
  supplier?: ISupplier,
  isRefundsEnabled?: boolean
) => {
  if (!onRefundClick) return [];

  const totalRefundedAmount = RefundCalculator.getTotalRefundsAmount(
    refunds.filter((r) => r.status === RefundStatus.Processed)
  );
  const refundValue = totalRefundedAmount
    ? formatPrice(MoneyHelper.toDecimal(totalRefundedAmount), currency)
    : null;

  const title =
    !totalRefundedAmount && supplier
      ? t("common.order_drawer.refund")
      : t("common.order_drawer.refunded");
  const hasSomeInReview =
    isRefundsEnabled &&
    !supplier?.canApproveRefunds &&
    refunds.some((r) => r.status === RefundStatus.InReview);
  const refundLabel = hasSomeInReview
    ? t("common.order_drawer.awaiting_approval")
    : refundValue
    ? `-${refundValue}`
    : "";

  return [
    {
      title,
      type: "read-only",
      value: (
        <FlexBox
          justifyContent={refundLabel ? "space-between" : "flex-end"}
          alignCenter
          fullWidth
        >
          {refundLabel && (
            <Typography
              variant="h4"
              style={{color: orange[500], fontWeight: 500}}
            >
              {refundLabel}
            </Typography>
          )}
          <Button color="primary" variant="text" onClick={onRefundClick}>
            {t("common.order_drawer.details")}
          </Button>
        </FlexBox>
      ),
    } as const,
  ];
};

export const exhibitorsDeliveryDetails = (
  order: ExpandedOrder,
  show: IShow,
  localMap: IResolvedLocalMap,
  role: Roles,
  t: TranslateFn,
  exhibitorPair?: IExhibitorPair
): Setting<Partial<ExpandedOrder & Omit<IShow, "createdAt">>>[] => {
  const statusMap = order.review
    ? getReviewOrderStatusMap(order.review)
    : order.paymentDetails.status === OrderStatus.cancelled
    ? orderStatusesMap.cancelled
    : null;
  const settings: (Setting<Partial<ExpandedOrder & IShow>> | null)[] = [
    statusMap
      ? {
          title: t("exhibitor.order.field.status"),
          value: <OrderStatusLabel statusMap={statusMap} />,
          type: "read-only",
        }
      : null,
    {
      title: t("exhibitor.order.field.venue"),
      value: show.venueName,
      type: "read-only",
    },
    {
      title: t("exhibitor.order.field.address"),
      value: ShowUtils.getAddress(show),
      type: "read-only",
    },
    {
      title: t("exhibitor.order.field.venue_contact"),
      value: getDisplayValue([
        show.contactName,
        show.contactPhoneNumber,
        show.contactEmail,
      ]),
      type: "read-only",
    },
    {
      title: localMap.show,
      value: show.name,
      type: "read-only",
    },
    !exhibitorPair?.stand?.builder?.hideStandNumber
      ? {
          title: localMap.stand,
          value: order.exhibitorConnection?.stand?.name,
          type: "read-only",
        }
      : null,
    !exhibitorPair?.stand?.builder?.hideStandNumber &&
    order.exhibitorConnection?.stand?.suffixName &&
    role !== Roles.EXHIBITOR
      ? {
          title: t("common.order_drawer.stand_number_suffix", {
            stand: localMap.stand,
          }),
          value: order.exhibitorConnection?.stand?.suffixName,
          type: "read-only",
        }
      : null,
  ];
  return settings.filter(filterNotEmptyArray);
};

export const downloadBankTransferPdf = async (
  invoice: Invoice,
  show: IShow,
  order: ExpandedOrder,
  localMap: IResolvedLocalMap,
  configById: CpoConfigById
) => {
  const file = await generateBankTransferInvoicePdfBlob({
    show,
    order,
    invoice,
    localMap,
    configById,
  });
  if (!file) {
    return;
  }
  const fileName = `inv - E#${invoice?.invoiceNumber}.pdf`;

  await downloadPdfFile(file, fileName);
};

export const downloadVatPdf = async (
  supplierCompany: ISupplierCompany,
  invoiceNumber: string | undefined,
  order: ExpandedOrder,
  user: Auth,
  show: IShow,
  localMap: IResolvedLocalMap,
  isSupplierView = false,
  configById: CpoConfigById,
  refunds: IRefund[]
) => {
  const orderStatusMap = getOrderStatusMap(order, refunds, user);

  const supplierCompanyLogo = supplierCompany?.logo?.url;

  let supplierCompanyResolvedLogo = "";

  if (supplierCompanyLogo) {
    supplierCompanyResolvedLogo =
      (await loadImageWithRedirect(supplierCompanyLogo)) ?? "";
  }

  const file = await generateVatPdfBlob({
    orderStatusMap,
    invoiceNumber,
    show,
    order,
    localMap,
    supplierCompany,
    venueName: show.venueName,
    venueAddress: ShowUtils.getAddress(show),
    isSupplierView,
    configById,
    supplierCompanyResolvedLogo,
  });
  const fileName = `inv - E#${invoiceNumber}.pdf`;

  await downloadPdfFile(file, fileName);
};

const calculateSpentAmount = (
  order: ExpandedOrder,
  salesTaxPercentage: number
) => {
  const {amount} = order.paymentDetails;
  const amountOfCancelled = order.products.reduce<number>((acc, product) => {
    if (product.status === OrderProductStatus.cancelled) {
      return acc + product.priceWithFees;
    }
    return acc;
  }, 0);
  return (
    amount -
    (amountOfCancelled + (amountOfCancelled / 100) * salesTaxPercentage)
  );
};
